import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Download, Leaf, Zap } from "lucide-react";
import { CURRENCIES, getScenarios } from "../lib/calculations";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { CaseSafe } from "./CaseSafe";

const HeroMetric = ({ eyebrow, value, unit, caption, icon: Icon, accent, testId }) => {
  const accentClass = { navy: "text-[#002D5B]", green: "text-[#059669]" }[accent];
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white p-8 md:p-10 flex flex-col gap-6"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase text-[#64748B]">
          <CaseSafe text={eyebrow} />
        </span>
        {Icon && <Icon className="w-5 h-5 text-[#94A3B8]" strokeWidth={1.5} />}
      </div>
      <div>
        <div
          className={`font-display text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-none ${accentClass}`}
        >
          {value}
          {unit && (
            <span className="ml-2 text-2xl md:text-3xl font-light tracking-normal align-baseline text-[#64748B]">
              {unit}
            </span>
          )}
        </div>
        {caption && (
          <p className="mt-4 text-xs md:text-sm text-[#64748B] leading-relaxed max-w-xl">
            {caption}
          </p>
        )}
      </div>
    </div>
  );
};

const BudgetRow = ({ label, value, accent = false, last = false, testId }) => (
  <div
    className={`flex items-center justify-between py-3.5 ${
      last ? "" : "border-b border-[#E2E8F0]"
    }`}
    data-testid={testId}
  >
    <span
      className={`text-sm ${
        accent ? "font-display text-base font-semibold text-[#0F172A]" : "text-[#475569]"
      }`}
    >
      {label}
    </span>
    <span
      className={`font-display tabular-nums ${
        accent ? "text-lg font-semibold text-[#002D5B]" : "text-base text-[#0F172A]"
      }`}
    >
      {value}
    </span>
  </div>
);

const BusinessCaseTile = ({ label, value, caption, accent, testId }) => {
  const accentClass = { navy: "text-[#002D5B]", orange: "text-[#EA580C]" }[accent || "navy"];
  return (
    <div
      className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 md:p-7"
      data-testid={testId}
    >
      <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#64748B]">
        <CaseSafe text={label} />
      </span>
      <div
        className={`mt-4 font-display text-4xl md:text-5xl font-light tracking-tighter ${accentClass}`}
      >
        {value}
      </div>
      {caption && (
        <p className="mt-3 text-xs text-[#64748B] leading-relaxed">{caption}</p>
      )}
    </div>
  );
};

export const ResultsDashboard = ({
  results,
  state,
  currencyCode,
  onDownloadPdf,
  pdfLoading,
  isEmailValid,
  hasGdprConsent,
  canDownloadPdf,
}) => {
  const { t, formatCurrency, formatKwh, formatNumber } = useI18n();
  const symbol = CURRENCIES[currencyCode].symbol;

  const scenarios = useMemo(() => getScenarios(results, state), [results, state]);
  const isNewBuild = scenarios.isNewBuild;

  const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0];
    return (
      <div className="rounded-lg border border-[#E2E8F0] bg-white/95 backdrop-blur px-4 py-2 shadow-sm">
        <div className="text-[10px] font-semibold tracking-widest uppercase text-[#64748B] mb-1">
          <CaseSafe text={p.payload.label} />
        </div>
        <div className="font-display font-semibold text-[#0F172A] text-sm tabular-nums">
          {formatKwh(p.value)} {t("units.kwh_yr")}
        </div>
      </div>
    );
  };

  const barData = useMemo(() => {
    const colors = isNewBuild
      ? ["#64748B", "#002D5B"]
      : ["#94A3B8", "#64748B", "#002D5B"];
    return scenarios.rows.map((row, i) => ({
      key: row.letter,
      label: `${row.letter} · ${t(row.nameKey)}`,
      kWh: Math.round(row.kwh),
      color: colors[i],
    }));
  }, [scenarios, isNewBuild, t]);

  const paybackLabel =
    results.payback === null
      ? t("common.na")
      : results.payback <= 0
      ? t("common.immediate")
      : `${formatNumber(results.payback, { maximumFractionDigits: 1, minimumFractionDigits: 1 })} ${t("common.years")}`;

  const co2Factor = formatNumber(results.co2PerKwh, { maximumFractionDigits: 3 });
  const co2Note = t("results.co2_note", { factor: co2Factor });

  return (
    <section id="dashboard-panel" className="space-y-10" data-testid="dashboard-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="text-[11px] font-semibold tracking-[0.25em] uppercase text-[#64748B]">
          {t("results.dashboard")}
        </div>

        <div className="flex flex-col items-start md:items-end gap-2 no-print">
          <Button
            onClick={onDownloadPdf}
            disabled={pdfLoading || !canDownloadPdf}
            className="h-11 bg-[#002D5B] hover:bg-[#001F40] text-white font-medium px-6 disabled:bg-[#94A3B8] disabled:opacity-100 disabled:cursor-not-allowed"
            data-testid="btn-download-pdf"
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={2} />
            {pdfLoading ? t("common.generating") : t("common.download_pdf")}
          </Button>
          {!canDownloadPdf && (
            <p
              className="text-[11px] text-[#EA580C] font-medium flex items-center gap-1.5 max-w-[300px] md:text-right"
              data-testid="email-gate-hint"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#EA580C] shrink-0 mt-1" />
              <span>
                {!isEmailValid
                  ? (state.contactEmail || "").trim().length === 0
                    ? t("gate.email_missing")
                    : t("gate.email_invalid")
                  : !hasGdprConsent
                  ? t("gate.consent_missing")
                  : ""}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* Comparison table */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-lg font-medium tracking-tight text-[#0F172A]">
            {isNewBuild ? t("results.comparison_2") : t("results.comparison_3")}
          </h2>
        </div>
        <div
          className="rounded-xl border border-[#E2E8F0] bg-white overflow-x-auto"
          data-testid="comparison-table-card"
        >
          <table className="w-full" data-testid="comparison-table">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <th className="py-3 px-4 text-left text-[10px] font-semibold tracking-[0.2em] uppercase text-[#64748B] whitespace-nowrap">
                  {t("results.option")}
                </th>
                <th className="py-3 px-4 text-right text-[10px] font-semibold tracking-[0.2em] uppercase text-[#64748B] whitespace-nowrap">
                  <CaseSafe text={t("results.annual_kwh")} />
                </th>
                <th className="py-3 px-4 text-right text-[10px] font-semibold tracking-[0.2em] uppercase text-[#64748B] whitespace-nowrap">
                  {t("results.annual_cost")}
                </th>
                <th className="py-3 px-4 text-right text-[10px] font-semibold tracking-[0.2em] uppercase text-[#64748B] whitespace-nowrap">
                  <CaseSafe text={t("results.co2_10yr")} />
                </th>
                <th className="py-3 px-4 text-right text-[10px] font-semibold tracking-[0.2em] uppercase text-[#64748B] whitespace-nowrap">
                  <CaseSafe text={t(scenarios.vsColumnHeaderKey)} />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {scenarios.rows.map((row) => (
                <tr
                  key={row.letter}
                  className={
                    row.recommended
                      ? "bg-[#002D5B]/[0.035] hover:bg-[#002D5B]/[0.06]"
                      : "hover:bg-[#F8FAFC]"
                  }
                  data-testid={`comparison-row-${row.letter.toLowerCase()}`}
                >
                  <td className="py-4 px-4 align-middle min-w-[220px]">
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex shrink-0 items-center justify-center w-7 h-7 rounded-full text-xs font-bold tracking-wider ${
                          row.recommended
                            ? "bg-[#002D5B] text-white"
                            : "bg-[#F1F5F9] text-[#64748B]"
                        }`}
                      >
                        {row.letter}
                      </span>
                      <span
                        className={`font-body text-sm ${
                          row.recommended ? "text-[#0F172A] font-semibold" : "text-[#0F172A]"
                        }`}
                      >
                        {t(row.nameKey)}
                      </span>
                      {row.recommended && (
                        <span className="text-[9px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 rounded bg-[#002D5B] text-white">
                          {t("common.recommended")}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right font-display tabular-nums text-sm">
                    {formatKwh(row.kwh)}
                  </td>
                  <td className="py-4 px-4 text-right font-display tabular-nums text-sm">
                    {formatCurrency(row.cost, symbol)}
                  </td>
                  <td className="py-4 px-4 text-right font-display tabular-nums text-sm">
                    {formatNumber(row.co2, { maximumFractionDigits: 1, minimumFractionDigits: 1 })} {t("units.tonne_short")}
                  </td>
                  <td
                    className={`py-4 px-4 text-right font-display tabular-nums text-sm font-semibold ${
                      row.reduction === null ? "text-[#94A3B8]" : "text-[#059669]"
                    }`}
                  >
                    {row.reduction === null
                      ? "—"
                      : `−${formatNumber(row.reduction, { maximumFractionDigits: 0 })}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-[#94A3B8] px-1" data-testid="co2-note">
          {co2Note}
        </p>
      </div>

      {!isNewBuild && (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-lg font-medium tracking-tight text-[#0F172A]">
              {t("results.total_impact_title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="total-impact-section">
            <HeroMetric
              eyebrow={t("results.total_annual_saving")}
              value={formatKwh(results.savedA_to_C)}
              unit={t("units.kwh_yr")}
              caption={t("results.total_annual_caption")}
              icon={Zap}
              accent="navy"
              testId="metric-total-annual-kwh"
            />
            <HeroMetric
              eyebrow={t("results.total_co2")}
              value={formatNumber(results.co2A_to_C, { maximumFractionDigits: 1, minimumFractionDigits: 1 })}
              unit={t("units.tonnes")}
              caption={t("results.total_co2_caption", { factor: co2Factor })}
              icon={Leaf}
              accent="green"
              testId="metric-total-co2"
            />
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-lg font-medium tracking-tight text-[#0F172A]">
            {isNewBuild ? t("results.env_impact_new") : t("results.env_impact_reno")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="env-impact-section">
          <HeroMetric
            eyebrow={isNewBuild ? t("results.additional_annual_saving") : t("results.extra_from_helvar")}
            value={formatKwh(results.savedB_to_C)}
            unit={t("units.kwh_yr")}
            caption={isNewBuild ? t("results.env_caption_new") : t("results.env_caption_reno")}
            icon={Zap}
            accent="navy"
            testId="metric-annual-kwh"
          />
          <HeroMetric
            eyebrow={isNewBuild ? t("results.additional_10yr") : t("results.extra_10yr_helvar")}
            value={formatNumber(results.co2B_to_C, { maximumFractionDigits: 1, minimumFractionDigits: 1 })}
            unit={t("units.tonnes")}
            caption={t("results.co2_caption", { factor: co2Factor })}
            icon={Leaf}
            accent="green"
            testId="metric-co2"
          />
        </div>
      </div>

      {/* Bar chart */}
      <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 md:p-6" data-testid="chart-card">
        <div className="flex items-baseline justify-between mb-4 px-2">
          <div>
            <h3 className="font-display text-base md:text-lg font-medium tracking-tight text-[#0F172A]">
              {isNewBuild ? t("results.chart_title_new") : t("results.chart_title_reno")}
            </h3>
            <p className="text-xs text-[#64748B] mt-0.5">{t("results.chart_subtitle")}</p>
          </div>
        </div>
        <div className="w-full h-[300px]" data-testid="energy-bar-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 28, right: 20, left: 10, bottom: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis
                dataKey="key"
                stroke="#94A3B8"
                tick={{ fill: "#64748B", fontSize: 16, fontWeight: 600 }}
                tickLine={false}
                axisLine={{ stroke: "#E2E8F0" }}
              />
              <YAxis
                stroke="#94A3B8"
                tick={{ fill: "#64748B", fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                width={60}
                label={{
                  value: t("units.kwh_yr"),
                  angle: -90,
                  position: "insideLeft",
                  offset: 10,
                  style: { fill: "#94A3B8", fontSize: 11 },
                }}
              />
              <Tooltip cursor={{ fill: "#F1F5F9" }} content={<ChartTooltip />} />
              <Bar
                dataKey="kWh"
                radius={[8, 8, 0, 0]}
                maxBarSize={110}
                isAnimationActive
                animationDuration={700}
              >
                {barData.map((d) => (
                  <Cell key={d.key} fill={d.color} />
                ))}
                <LabelList
                  dataKey="kWh"
                  position="top"
                  formatter={(v) => formatKwh(v)}
                  style={{ fill: "#0F172A", fontSize: 12, fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div
          className="mt-2 px-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2"
          data-testid="chart-legend"
        >
          {barData.map((d) => (
            <div key={d.key} className="flex items-start gap-2 text-xs leading-snug text-[#475569]">
              <span
                className="mt-0.5 inline-block w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: d.color }}
                aria-hidden="true"
              />
              <span>
                <span className="font-semibold text-[#0F172A]">{d.key}</span>
                {" · "}
                <span>{d.label.replace(/^[A-Z]\s·\s/, "")}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Budget */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-lg font-medium tracking-tight text-[#0F172A]">
            {t("capex.title")}
          </h2>
        </div>
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-6 md:p-8" data-testid="budget-section">
          <BudgetRow
            label={t("capex.luminaire_cost")}
            value={formatCurrency(results.luminaireCost, symbol)}
            testId="budget-luminaires"
          />
          <BudgetRow
            label={t("capex.installation")}
            value={formatCurrency(results.installationCost, symbol)}
            testId="budget-installation"
          />
          <BudgetRow
            label={t("capex.helvar_system")}
            value={formatCurrency(results.helvarSystemCost, symbol)}
            testId="budget-helvar"
          />
          <BudgetRow
            label={t("results.total_capex")}
            value={formatCurrency(results.totalProjectCapex, symbol)}
            accent
            last
            testId="budget-total"
          />
        </div>
      </div>

      {/* Business Case */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-lg font-medium tracking-tight text-[#0F172A]">
            {t("results.business_case")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="business-case-section">
          <BusinessCaseTile
            label={results.compareCompetitor ? t("results.controls_premium") : t("results.helvar_investment")}
            value={formatCurrency(results.controlsPremiumCapex, symbol)}
            caption={
              results.compareCompetitor
                ? t("results.controls_premium_caption")
                : t("results.helvar_investment_caption")
            }
            accent="orange"
            testId="tile-controls-premium"
          />
          <BusinessCaseTile
            label={t("results.payback_period")}
            value={paybackLabel}
            caption={
              t("results.payback_driver", {
                amount: formatCurrency(results.financialB_to_C, symbol),
              }) +
              (results.compareCompetitor
                ? ""
                : t("results.payback_worst_case_addendum"))
            }
            accent="navy"
            testId="tile-payback"
          />
        </div>
      </div>
    </section>
  );
};

export default ResultsDashboard;
